<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    hideTooltip: {
      type: Boolean,
      default: false,
    },
    multiline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: false,
      isOverflowing: false,
    };
  },
  watch: {
    value: {
      handler() {
        this.checkOverflow();
      },
      flush: 'post',
    },
  },
  mounted() {
    this.checkOverflow();
    window.addEventListener('resize', this.checkOverflow);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkOverflow);
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    toggleExpand(e) {
      e.preventDefault();
      this.isExpanded = !this.isExpanded;
    },
    checkOverflow() {
      if (!this.multiline) return;

      const el = this.$refs.textSpan;
      if (el) {
        this.isOverflowing = el.scrollWidth > el.clientWidth;
      }
    },
  },
};
</script>

<template>
  <div
    class="relative group w-full ltr:-ml-1 rtl:-mr-1 flex items-start"
    :class="isExpanded ? 'h-auto py-0.5' : 'h-5'"
  >
    <a
      v-if="href"
      :href="href"
      class="flex items-center gap-2 text-n-slate-11 hover:underline w-full"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
        :class="{ 'mt-0.5': isExpanded }"
      />

      <span
        v-if="value"
        class="text-sm"
        :class="[
          isExpanded
            ? 'whitespace-normal break-all'
            : 'overflow-hidden whitespace-nowrap text-ellipsis flex-1',
        ]"
      >
        {{ value }}
      </span>
      <span v-else class="text-sm text-n-slate-11 flex-1">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>

      <div class="flex items-center ltr:-ml-1 rtl:-mr-1 flex-shrink-0">
        <NextButton
          v-if="showCopy"
          ghost
          xs
          slate
          icon="i-lucide-clipboard"
          @click.prevent="onCopy"
        />
      </div>
    </a>

    <div
      v-else
      class="flex gap-2 text-n-slate-11 w-full"
      :class="isExpanded ? 'items-start' : 'items-center'"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
        :class="{ 'mt-[3px]': isExpanded }"
      />

      <span
        v-if="value"
        ref="textSpan"
        v-dompurify-html="value"
        class="text-sm flex-1"
        :class="[
          isExpanded
            ? 'whitespace-normal break-all'
            : 'overflow-hidden whitespace-nowrap text-ellipsis',
        ]"
      />
      <span v-else class="text-sm text-n-slate-11 flex-1">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>

      <div class="flex items-center gap-0.5 flex-shrink-0 ltr:-ml-1 rtl:-mr-1">
        <NextButton
          v-if="showCopy && value"
          ghost
          xs
          slate
          icon="i-lucide-clipboard"
          @click.stop="onCopy"
        />

        <NextButton
          v-if="multiline && (isOverflowing || isExpanded)"
          ghost
          xs
          slate
          :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          @click.stop="toggleExpand"
        />
      </div>
    </div>

    <div
      v-if="title && !hideTooltip && !isExpanded"
      class="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden whitespace-normal rounded-md bg-slate-800 px-3 py-2 text-center text-xs text-white shadow-lg group-hover:block w-max max-w-[250px]"
    >
      {{ title }}

      <div
        class="absolute top-full left-4 -mt-px -ml-1 border-4 border-transparent border-t-slate-800"
      />
    </div>
  </div>
</template>
